import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AUTH_TOKEN_KEY, authAPI } from '../lib/api';
import { Clock, Warning, X } from '@phosphor-icons/react';

// Parse JWT to get expiration time
const getTokenExpiration = () => {
  const token = sessionStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

// Format remaining time
const formatTime = (ms) => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Warning shows 5 minutes before expiry
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;
// Check every 30 seconds
const CHECK_INTERVAL_MS = 30 * 1000;
// Update countdown every second when warning is shown
const COUNTDOWN_INTERVAL_MS = 1000;

export default function SessionTimeoutWarning() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExtending, setIsExtending] = useState(false);
  const checkIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Check if session is about to expire
  const checkSession = useCallback(() => {
    if (!user) return;
    
    const expTime = getTokenExpiration();
    if (!expTime) return;
    
    const now = Date.now();
    const remaining = expTime - now;
    
    if (remaining <= 0) {
      // Session expired - logout
      setShowWarning(false);
      logout();
    } else if (remaining <= WARNING_THRESHOLD_MS) {
      // Show warning
      setShowWarning(true);
      setTimeRemaining(remaining);
    } else {
      setShowWarning(false);
    }
  }, [user, logout]);

  // Start session check interval
  useEffect(() => {
    if (!user) {
      setShowWarning(false);
      return;
    }

    // Initial check
    checkSession();

    // Set up periodic checks
    checkIntervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [user, checkSession]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showWarning) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      const expTime = getTokenExpiration();
      if (!expTime) return;
      
      const remaining = expTime - Date.now();
      
      if (remaining <= 0) {
        setShowWarning(false);
        logout();
      } else {
        setTimeRemaining(remaining);
      }
    }, COUNTDOWN_INTERVAL_MS);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [showWarning, logout]);

  // Extend session by refreshing the token
  const extendSession = async () => {
    setIsExtending(true);
    try {
      const response = await authAPI.refresh();
      if (response.data?.access_token) {
        sessionStorage.setItem(AUTH_TOKEN_KEY, response.data.access_token);
        setShowWarning(false);
      }
    } catch (error) {
      // Session extension failed - close warning, user can continue until actual expiry
      if (process.env.NODE_ENV === 'development') {
        process.env.NODE_ENV === "development" && console.error('Failed to extend session:', error);
      }
      setShowWarning(false);
    } finally {
      setIsExtending(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setShowWarning(false);
    logout();
  };

  // Dismiss warning (session will still expire)
  const dismissWarning = () => {
    setShowWarning(false);
  };

  if (!showWarning || !user) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-200"
        data-testid="session-timeout-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <div className="p-2 bg-white/20 rounded-lg">
              <Warning size={24} weight="fill" />
            </div>
            <h2 className="text-lg font-semibold">Session Expiring Soon</h2>
          </div>
          <button 
            onClick={dismissWarning}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-4">
              <Clock size={40} className="text-amber-600" weight="duotone" />
            </div>
            <p className="text-slate-600 mb-2">
              Your session will expire in
            </p>
            <div className="text-4xl font-bold text-amber-600 font-mono" data-testid="countdown-timer">
              {formatTime(timeRemaining)}
            </div>
          </div>

          <p className="text-sm text-slate-500 text-center mb-6">
            For your security, you will be automatically logged out when the timer reaches zero. 
            Click &quot;Extend Session&quot; to continue working.
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={extendSession}
              disabled={isExtending}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="extend-session-btn"
            >
              {isExtending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Extending...
                </>
              ) : (
                'Extend Session'
              )}
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
              data-testid="logout-now-btn"
            >
              Log Out Now
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-400 text-center">
            Sessions expire after 24 hours of inactivity for your security
          </p>
        </div>
      </div>
    </div>
  );
}
